import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import moment from 'moment';

import { Separator } from '../ui/separator';
import { Filter, Upload, Search, RotateCcw, Loader2 } from 'lucide-react';
import { Input } from '../ui/input';
import { motion, AnimatePresence } from 'framer-motion';

import { ColumnDef } from '@tanstack/react-table';
import { DataTable } from './OrderTableNew';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';

import { DataTableColumnHeader } from '../ui/data-column-header';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

// Import the same selectors and thunks as the old orders page
import { selectAllOrders } from '../../modules/orders/store/slice';
import { doGetOrders, doDeleteOrder, doRetryOrder } from '../../modules/orders/store/thunk';
import { ORDER_STATUSES, DAY_FORMAT, ROLES, PAGE_SIZE } from '../../constants';
import { AppDispatch, RootState } from '../../redux/store';
import { getStatusBadgeStyles } from '../../utils/statusStyles';
import { cn } from '../../lib/utils';
import { formatOrderStatus } from '../Elements/OrderTimeLine';

// Import the existing ImportOrders components
import ImportOrders from './Orders/ImportOrders';

// Import the OrderAddressRetry component
import { OrderAddressRetry } from './OrderRetry';
import OrdersFilter from './OrdersNewFilter';
import withPage from './withPage';
import { selectAllCarriers } from '../../modules/carriers/stores/slice';
import { doGetCarriers } from '../../modules/carriers/stores/thunk';

export type Order = {
  id: string;
  orderId: string;
  orderRef: string;
  deliveryDate: string;
  deliveryName: string;
  deliveryAddress: string;
  status: string;
  updatedAt: string;
  externalIds?: any[];
  location?: {
    company?: {
      name: string;
      logoThumbUrl?: string;
      logoUrl?: string;
    };
  };
  claim?: {
    status: string;
  };
  carrierIntegration?: {
    carrierName?: string;
  };
};

const NewOrdersPage = ({ query }) => {
  const dispatch = useDispatch<AppDispatch>();
  const history = useHistory();

  // Use the same selectors as the old orders page
  const isFetching = useSelector((state: RootState) => state.order.isFetching);
  const userRole = useSelector((state: RootState) => state.auth.user.role);
  const user = useSelector((state: RootState) => state.auth.user);
  const action = useSelector((state: RootState) => state.order.action);
  const status = useSelector((state: RootState) => state.order.status);
  const metadata = useSelector((state: RootState) => state.order.metadata);
  const orders = useSelector((state: RootState) => selectAllOrders(state));
  const carriers = useSelector((state: RootState) => selectAllCarriers(state));

  const [showFilter, setShowFilter] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(PAGE_SIZE);
  const [transactionData, setTransactionData] = useState([]);

  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Retry modal state
  const [retryOrderAddress, setRetryOrderAddress] = useState<Order | null>(null);

  // Filter orders to exclude REJECTED status (same as old page)
  const filteredOrders = useMemo(() => {
    return orders.filter((order) => order.status !== ORDER_STATUSES.REJECTED);
  }, [orders]);

  // Helper function to check if order can be removed
  const canBeRemoved = (order: Order) => {
    return (
      (!order.externalIds || order.externalIds.length === 0) &&
      [ORDER_STATUSES.DRAFT, ORDER_STATUSES.ERROR, ORDER_STATUSES.ADDRESS_NOT_FOUND].includes(order.status)
    );
  };

  const getCarrierLogo = (carrierName: string) => {
    const carrier = carriers.find((c) => c.carrierName === carrierName);
    return carrier?.logo;
  };

  // Simplified retry function that matches old behavior exactly
  const onClickRetry = async (record: Order) => {
    if (record.status === 'ADDRESS_NOT_FOUND') {
      // handle address retry
      setRetryOrderAddress(record);
    } else if (record.status === 'ERROR') {
      // handle error retry - execute immediately like old implementation
      await dispatch(doRetryOrder({ orderId: record.orderId }));
      // Refresh orders list after error retry
      await dispatch(
        doGetOrders({
          page: currentPage,
          take: itemsPerPage,
          ...query,
        }),
      );
    }
  };

  // Delete function - moved to main component
  const handleClickDelete = (id: string) => {
    dispatch(doDeleteOrder({ id }));
  };

  // Create columns with access to component state
  const columns: ColumnDef<Order>[] = useMemo(
    () => [
      {
        accessorKey: 'orderId',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Order ID" />,
        cell: ({ row }) => {
          const order = row.original;
          return (
            <button
              className="text-black-200 font-bold hover:text-gray-700 cursor-pointer bg-transparent border-none p-0 text-left truncate"
              onClick={(e) => {
                // Check if Ctrl (Windows/Linux) or Cmd (Mac) is pressed
                if (e.ctrlKey || e.metaKey) {
                  window.open(`/orders/${order.orderId}`, '_blank');
                } else {
                  window.location.href = `/orders/${order.orderId}`;
                }
              }}
            >
              {order.orderId}
            </button>
          );
        },
        enableSorting: false,
        enableHiding: false,
        size: 110,
      },
      {
        accessorKey: 'status',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
        cell: ({ row }) => {
          const status = row.getValue('status') as string;
          return (
            <Badge variant="outline" className={cn('text-sm whitespace-nowrap', getStatusBadgeStyles(status))}>
              {formatOrderStatus(status)}
            </Badge>
          );
        },
        enableSorting: false,
        enableHiding: false,
        size: 90,
      },
      {
        accessorKey: 'orderRef',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Order Ref" />,
        cell: ({ row }) => {
          const value = row.getValue('orderRef') as string;
          return <div className="truncate">{value}</div>;
        },
        enableSorting: false,
        enableHiding: false,
        size: 110,
      },
      {
        accessorKey: 'company',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Company" />,
        cell: ({ row }) => {
          const company = row.original.location?.company;
          if (company) {
            return (
              <div className="flex items-center space-x-2 min-w-0">
                {company.logoThumbUrl && (
                  <img src={company.logoThumbUrl} alt={company.name} className="w-6 h-6 rounded-full flex-shrink-0" />
                )}
                <span className="truncate">{company.name}</span>
              </div>
            );
          }
          return null;
        },
        enableSorting: false,
        enableHiding: false,
        size: 130,
      },
      {
        accessorKey: 'deliveryDate',
        header: ({ column }) => (
          <div className="min-w-[80px] whitespace-nowrap">
            <DataTableColumnHeader column={column} title="Delivery Date" />
          </div>
        ),
        cell: ({ row }) => {
          const value = row.getValue('deliveryDate') as string;
          return <div className="truncate min-w-[80px]">{value ? moment(value).format(DAY_FORMAT) : 'n/a'}</div>;
        },
        enableSorting: false,
        enableHiding: false,
        size: 80,
      },
      {
        accessorKey: 'deliveryAddress',
        header: ({ column }) => (
          <div className="whitespace-nowrap">
            <DataTableColumnHeader column={column} title="Delivery Address" />
          </div>
        ),
        cell: ({ row }) => {
          const value = row.getValue('deliveryAddress') as string;
          return (
            <div className="truncate max-w-[240px]" title={value}>
              {value}
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false,
        size: 240,
      },
      // Carrier column - only for admin users, positioned before actions
      ...(userRole === 'ADMIN'
        ? [
            {
              accessorKey: 'carrier',
              header: ({ column }) => (
                <div className="whitespace-nowrap">
                  <DataTableColumnHeader column={column} title="Carrier" />
                </div>
              ),
              cell: ({ row }) => {
                const carrierName = row.original?.carrierIntegration?.carrierName;
                if (!carrierName) return null;

                const carrierLogo = getCarrierLogo(carrierName);

                if (carrierLogo) {
                  return (
                    <img
                      src={carrierLogo}
                      alt={carrierName}
                      className="w-6 h-6 rounded-full object-cover"
                      onError={(e) => {
                        // Fallback to letter avatar if image fails
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  );
                }

                const firstLetter = carrierName.charAt(0).toUpperCase();
                return (
                  <div
                    className="w-6 h-6 rounded-full bg-blue-300 text-white text-xs font-medium flex items-center justify-center"
                    title={carrierName}
                  >
                    {firstLetter}
                  </div>
                );
              },
              enableSorting: false,
              enableHiding: false,
              size: 50,
            },
          ]
        : []),
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const order = row.original;

          return (
            <div className="flex items-center space-x-2 h-8">
              {/* Retry button - shown for ADDRESS_NOT_FOUND and ERROR statuses */}
              {order.status === 'ADDRESS_NOT_FOUND' &&
                (userRole === ROLES.ADMIN || userRole === ROLES.COMPANY_OWNER) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    onClick={() => {
                      onClickRetry(order);
                    }}
                    title="Retry address"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                )}

              {order.status === 'ERROR' &&
                (!order.externalIds || order.externalIds.length === 0) &&
                (userRole === ROLES.ADMIN || userRole === ROLES.COMPANY_OWNER) && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        title="Retry order"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Retry to register this order with carrier</AlertDialogTitle>
                        <AlertDialogDescription>Are you sure to retry this order?</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => {
                            onClickRetry(order);
                          }}
                        >
                          Yes
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false,
        size: 80,
      },
    ],
    [userRole, onClickRetry, handleClickDelete, canBeRemoved],
  );

  useEffect(() => {
    if (userRole === 'ADMIN') {
      dispatch(doGetCarriers({}));
    }
  }, []);

  // Load data on component mount
  useEffect(() => {
    dispatch(
      doGetOrders({
        page: currentPage,
        take: itemsPerPage,
        ...query,
      }),
    );
  }, [user.selectedCompany, currentPage, itemsPerPage]);

  // Refresh data when actions complete
  useEffect(() => {
    if (['create', 'update', 'import', 'delete', 'delete-multi'].includes(action) && status === 'succeeded') {
      dispatch(
        doGetOrders({
          page: currentPage,
          take: itemsPerPage,
          ...query,
        }),
      );
      if (selectedRowKeys.length) setSelectedRowKeys([]);
    }
  }, [isFetching]);

  // Refresh data when retry order address is null (same as old page)
  useEffect(() => {
    if (retryOrderAddress === null) {
      dispatch(
        doGetOrders({
          page: currentPage,
          take: itemsPerPage,
          ...query,
        }),
      );
    }
  }, [retryOrderAddress]);

  const handleSearch = () => {
    dispatch(
      doGetOrders({
        page: 1,
        take: itemsPerPage,
        q: searchValue || '',
      }),
    );
    setCurrentPage(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleFilter = (params, updatePath) => {
    delete params.pageSizeOptions;
    delete params.showSizeChanger;
    delete params.total;
    delete params.locale;
    delete params.page;
    if (updatePath) {
      // Fix: Use proper history.push syntax with query string
      const searchString = new URLSearchParams(params).toString();
      history.push({
        pathname: history.location.pathname,
        search: searchString ? `?${searchString}` : '',
      });
    }
    const newCurrentPage = Number(params?.current || 1);
    const newItemsPerPage = Number(params?.pageSize) || PAGE_SIZE;
    dispatch(
      doGetOrders({
        page: newCurrentPage,
        take: newItemsPerPage,
        ...params,
      }),
    );
  };

  const handlePaginationChange = (pagination: { current: number; pageSize: number }) => {
    setCurrentPage(pagination.current);
    setItemsPerPage(pagination.pageSize);
    dispatch(
      doGetOrders({
        page: pagination.current,
        take: pagination.pageSize,
        ...query,
      }),
    );
  };

  const handleUploadClick = () => {
    setShowUploadModal(true);
  };

  const handleCancelUpload = () => {
    setShowUploadModal(false);
  };

  const handleUploadSuccess = () => {
    setShowUploadModal(false);
    // Refresh orders list
    dispatch(
      doGetOrders({
        page: currentPage,
        take: itemsPerPage,
        ...query,
      }),
    );
  };

  const handleRowClick = (e: React.MouseEvent, orderId: string) => {
    // Check if Ctrl (Windows/Linux) or Cmd (Mac) is pressed
    if (e.ctrlKey || e.metaKey) {
      window.open(`/orders/${orderId}`, '_blank');
    } else {
      window.location.href = `/orders/${orderId}`;
    }
  };

  return (
    // <motion.div layout>
    <div className="bg-white min-h-screen">
      <div className="flex justify-center px-4 py-10">
        <div className="w-full max-w-screen-xl bg-white">
          {/* Top Bar */}
          <div className="flex justify-between items-center">
            <div className="text-left">
              <h1 className="text-2xl font-semibold">Orders</h1>
              <p className="text-sm text-muted-foreground">View, filter, create, upload and act upon orders</p>
            </div>

            <div className="flex">
              <div className="relative mr-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  className="pl-10 w-96"
                  placeholder="Search orders..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </div>

              <Button
                className="mr-4"
                size="icon"
                variant={showFilter ? 'secondary' : 'outline'}
                onClick={() => setShowFilter(!showFilter)}
              >
                <Filter />
              </Button>

              {userRole !== 'CARRIER_USER' && (
                <>
                  {/* <Button className="mr-4">
                      <Plus />
                      Create new order
                    </Button> */}
                  <Button variant="secondary" onClick={handleUploadClick}>
                    <Upload />
                    Upload orders
                  </Button>
                </>
              )}
            </div>
          </div>

          <AnimatePresence>
            {showFilter && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
              >
                {/* <OrderFilter onSubmit={handleFilter} /> */}
                <OrdersFilter
                  isFetching={isFetching}
                  query={query}
                  onSubmit={handleFilter}
                  setTransactionData={setTransactionData}
                />
              </motion.div>
            )}
          </AnimatePresence>

          <Separator className="mt-4" />

          {isFetching && (
            <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-50">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          )}

          <div className="container mx-auto py-10">
            <DataTable<Order, unknown>
              columns={columns}
              data={filteredOrders}
              pagination={{
                current: currentPage,
                pageSize: itemsPerPage,
                total: metadata?.totalItems || 0,
              }}
              onPaginationChange={handlePaginationChange}
            />
          </div>

          {/* Upload Modal */}
          <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
            <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Upload Orders</DialogTitle>
                <DialogDescription>Upload orders from Excel or CSV files.</DialogDescription>
              </DialogHeader>

              <ImportOrders onCancel={handleUploadSuccess} />
            </DialogContent>
          </Dialog>

          {/* Address Retry Modal */}
          <OrderAddressRetry order={retryOrderAddress} close={() => setRetryOrderAddress(null)} />
        </div>
      </div>
    </div>
  );
};

export default withPage(NewOrdersPage);
